/**
 * Scope tracking for the proof. grammar parser.
 *
 * A scope is defined by a markdown heading path. The parser uses a scope
 * stack to track the current path and assign it to each block.
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import type { ScopePath } from "../ast/types.js";

export interface ScopeFrame {
  level: number;
  text: string;
}

export class ScopeStack {
  private readonly stack: ScopeFrame[] = [];

  /**
   * Push a new heading onto the scope stack.
   * Pops any frames at the same or deeper level first (markdown rules).
   */
  push(level: number, text: string): void {
    // Pop frames at the same or deeper level
    while (this.stack.length > 0) {
      const top = this.stack[this.stack.length - 1];
      if (top !== undefined && top.level >= level) {
        this.stack.pop();
      } else {
        break;
      }
    }
    this.stack.push({ level, text });
  }

  /** Current scope path as a readonly array. */
  current(): ScopePath {
    return this.stack.map((f) => f.text);
  }

  /** Reset to root scope. */
  reset(): void {
    this.stack.length = 0;
  }
}
